import React, { useState, useCallback, useEffect } from "react";
import Layout from "../../../components/layout/layout";
import { TabGroup } from "../../../components/tabs/Tabs";
import { SearchHeader } from "../../../components/seachHeader/SearchHeader";
import {Pencil,Star,Trash,Plus,X,Search} from "lucide-react";
import { FaSortUp } from 'react-icons/fa';
import { FaSortDown } from 'react-icons/fa';
import tooltipDataNotFoundImg from "../../../assets/image/tooltipDataNotFoundImg.png";
import { db } from "../../../config/firebase";
import { doc, setDoc, getDoc, updateDoc, arrayUnion, collection, query, where, getDocs, onSnapshot, deleteDoc, arrayRemove } from "firebase/firestore";
import { useAppState } from "../../../hooks/useAppState";

interface TooltipPackage {
  packageId: string;
  packageName: string;
  created: string;
  // raw timestamp for sorting (milliseconds since epoch)
  createdAtRaw?: number;
  lastSale: string;
  ratings: number;
  sales: number;
  earnings: string;
  status: boolean;
  price: number;
  currency: string;
  primaryUrl: string;
  tooltips: unknown[];
  description?: string;
  admindisabled?: boolean;
}

// Utility: Parse Firestore Timestamp-like values into JS Date
const parseFirestoreTimestamp = (value: unknown): Date | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'object') {
    const v = value as { toDate?: () => Date; seconds?: number; nanoseconds?: number };

    // Firestore Timestamp-like object with toDate()
    if (typeof v.toDate === 'function') {
      try {
        return v.toDate();
      } catch {
        // fall through
      }
    }

    // Object with seconds/nanoseconds
    if (typeof v.seconds === 'number') {
      const secs = v.seconds || 0;
      const nanos = typeof v.nanoseconds === 'number' ? v.nanoseconds : 0;
      return new Date(secs * 1000 + Math.floor(nanos / 1e6));
    }
  }
  // If it's a number, treat as milliseconds or seconds (heuristic)
  if (typeof value === 'number') {
    // if value looks like seconds (10 digits) convert to ms
    if (value < 1e12) {
      return new Date(value * 1000);
    }
    return new Date(value);
  }
  // If it's a string, try to parse
  if (typeof value === 'string') {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
};

// Normalize a user-entered URL to a fully-qualified https URL.
// Examples:
//  - "netflix.com" -> "https://www.netflix.com"
//  - "http://example.com" -> "https://www.example.com" (protocol forced to https)
//  - "https://sub.domain.com/path" -> unchanged (except protocol normalized)
const normalizeUrl = (input?: string): string => {
  if (!input) return "";

  // Trim whitespace
  let value = input.trim();

  // If protocol missing, prepend https://
  if (!/^https?:\/\//i.test(value)) {
    value = `https://${value}`;
  }

  try {
    const u = new URL(value);

    // Force https protocol
    u.protocol = 'https:';

    // If hostname looks like a bare domain (example.com) without subdomain, add www.
    // This is a conservative heuristic: hostname parts length === 2 (domain + tld)
    const hostParts = u.hostname.split('.').filter(Boolean);
    const isIp = hostParts.every(part => /^\d+$/.test(part));
    if (!isIp && hostParts.length === 2 && !u.hostname.startsWith('www.')) {
      u.hostname = `www.${u.hostname}`;
    }

    // Remove trailing slash if it's just the root and input didn't include a path
    return u.toString();
  } catch {
    // If URL constructor fails, return original best-effort value with https://
    return value;
  }
};

// Simple validation for normalized URL
const isValidNormalizedUrl = (value?: string) => {
  if (!value) return false;
  const regex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
  return regex.test(value);
};

// Optimized: Fetch all purchase data for packages in ONE query
const getPurchaseDataForPackages = async (packageIds: string[]) => {
  if (packageIds.length === 0) {
    return new Map();
  }

  try {
    const purchasesRef = collection(db, 'tooltipPurchases');
    
    // Firestore 'in' query supports up to 10 items, so we need to batch
    const batchSize = 10;
    const batches: string[][] = [];
    
    for (let i = 0; i < packageIds.length; i += batchSize) {
      batches.push(packageIds.slice(i, i + batchSize));
    }

    const allPurchases: Map<string, TooltipPurchase[]> = new Map();
    
    // Fetch all batches in parallel
    await Promise.all(
      batches.map(async (batch) => {
        const purchaseQuery = query(
          purchasesRef,
          where('packageId', 'in', batch)
        );
        
        const purchaseSnapshot = await getDocs(purchaseQuery);
        
        purchaseSnapshot.docs.forEach((doc) => {
          const data = doc.data() as TooltipPurchase;
          const pkgId = data.packageId || '';
          
          if (!allPurchases.has(pkgId)) {
            allPurchases.set(pkgId, []);
          }
          allPurchases.get(pkgId)!.push(data);
        });
      })
    );

    return allPurchases;
  } catch (error) {
    console.error('Error fetching purchase data:', error);
    return new Map();
  }
};

// Compute package statistics from purchases
const computePackageStats = (purchases: TooltipPurchase[]) => {
  if (purchases.length === 0) {
    return {
      lastSale: 'N/A',
      salesCount: 0,
      averageRating: 0,
      totalEarnings: 0
    };
  }

  let latestDate: Date | null = null;
  let totalRating = 0;
  let ratingCount = 0;
  let totalEarnings = 0;

  purchases.forEach((purchase) => {
    // Latest purchase date
    const purchaseDate = parseFirestoreTimestamp(purchase.purchasedAt);
    if (purchaseDate && (!latestDate || purchaseDate > latestDate)) {
      latestDate = purchaseDate;
    }

    // Ratings
    const rating = purchase.rating;
    const ratingNum = typeof rating === 'number' ? rating : (rating ? Number(rating) : NaN);
    if (!isNaN(ratingNum)) {
      totalRating += ratingNum;
      ratingCount += 1;
    }

    // Earnings
    const priceVal = purchase.price;
    const priceNum = typeof priceVal === 'number' ? priceVal : (priceVal ? Number(priceVal) : NaN);
    if (!isNaN(priceNum)) {
      totalEarnings += priceNum;
    }
  });

  return {
    lastSale: latestDate
      ? (latestDate as Date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      : 'N/A',
    salesCount: purchases.length,
    averageRating: ratingCount > 0 ? Math.round((totalRating / ratingCount) * 10) / 10 : 0,
    totalEarnings
  };
};

interface TooltipSalesData {
  packageId: string;
  packageName: string;
  customerName: string;
  purchased: string;
  // raw timestamp for accurate sorting and potential further use
  purchasedAt?: string | number | Date;
  ratings: number;
  reviews: string;
  earnings: string;
  price: number;
  orgName:string;
  primaryUrl:string;
}

// Define a typed shape for purchases to avoid `any`
interface TooltipPurchase {
  price?: number | string;
  rating?: number | string;
  purchasedAt?: unknown;
  purchaserId?: string;
  review?: string;
  packageId?: string;
  description?: string;
}

// Shape of tooltip package documents in Firestore
interface TooltipPackageDoc {
  packageId?: string;
  name?: string;
  createdAt?: string | number | Date;
  status?: string;
  price?: number | string;
  currency?: string;
  primaryUrl?: string;
  tooltips?: unknown[];
  description?: string;
}

const TooltipExpert = () => {
   const {user} = useAppState();
   console.log(user,"efwrdwsdxw")
  const [activeTab, setActiveTab] = useState("package");
  const [createTooltipModel, setCreateTooltipModel] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
  // Separate pagination state for each tab
  const [packagePage, setPackagePage] = useState(1);
  const [salesPage, setSalesPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    name: '',
    primaryUrl: '',
    price: '',
    description: ''
  });
  // Add tooltip type selection state (support multiple selections)
  const [selectedTooltipTypes, setSelectedTooltipTypes] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [urlError, setUrlError] = useState(""); // State for URL validation error
  const [tooltipPackages, setTooltipPackages] = useState<TooltipPackage[]>([]);
  const [tooltipSalesData, setTooltipSalesData] = useState<TooltipSalesData[]>([]);
  const [fetchingPackages, setFetchingPackages] = useState(false);
  const [fetchingSales, setFetchingSales] = useState(false);
  // Delete confirmation state
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<TooltipPackage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Status toggle confirmation modal state
  const [statusConfirmModal, setStatusConfirmModal] = useState(false);
  const [statusTargetPackage, setStatusTargetPackage] = useState<{
    packageId: string;
    currentStatus: boolean;
    packageName?: string;
  } | null>(null);
  const [statusChanging, setStatusChanging] = useState(false);
console.log(tooltipPackages,"tooltipPackages")
  // Sales filter states
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  // Tooltip injection management states
  const [tooltipInjectionStatus, setTooltipInjectionStatus] = useState<{
    isInjected: boolean;
    hasData: boolean;
    isChecking: boolean;
  }>({ isInjected: false, hasData: false, isChecking: false });
  const [isReinjecting, setIsReinjecting] = useState(false);
  const [uniqueOrgs, setUniqueOrgs] = useState<{ value: string; label: string }[]>([]);
  // Applied filters state for package tooltip filters
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [appliedFilters, setAppliedFilters] = useState<any>(null);
  // Sorting state for package table (sales / earnings)
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const tabs = [
    { label: "Package Summary", value: "package" },
    { label: "Sales History", value: "sales" },
  ];

  // Implement search handler used by SearchHeader
  const handleSearchImpl = (value: string) => {
    const q = value?.toString() || "";
    setSearchQuery(q);
    // Reset pagination for current tab when search changes
    if (activeTab === "package") {
      setPackagePage(1);
    } else {
      setSalesPage(1);
    }
  };

  const handleCreatePackage = () => {
    setIsEditMode(false);
    setEditingPackageId(null);
    setFormData({
      name: '',
      // Prefill with scheme to guide the user
      primaryUrl: 'https://',
      price: '',
      description: ''
    });
    setCreateTooltipModel(true);
  };

  const handleEditPackage = (packageData: TooltipPackage) => {
    setIsEditMode(true);
    setEditingPackageId(packageData.packageId);
    setFormData({
      name: packageData.packageName,
      primaryUrl: packageData.primaryUrl,
      price: packageData.price.toString(),
      description: packageData.description || ''
    });
    setCreateTooltipModel(true);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === "primaryUrl") {
      // If the user is only seeing the scheme placeholder (https://) treat it as empty for validation
      const trimmed = value ? value.trim() : '';
      if (trimmed === 'https://' || trimmed === 'http://') {
        // don't show an error yet; wait for user to type more
        setUrlError('');
        return;
      }

      // Normalize and validate URL
      const normalized = normalizeUrl(value);
      if (!isValidNormalizedUrl(normalized)) {
        setUrlError("Please enter a valid URL.");
      } else {
        setUrlError("");
      }
    }
  };

  // Generate unique tooltip ID
  const generateTooltipId = () => {
    return `P${Date.now()}${Math.floor(Math.random() * 1000)}`;
  };

  // Real-time listener for tooltip packages using onSnapshot
  const setupPackagesListener = useCallback(() => {
    if (!user?.uid) return;
    
    setFetchingPackages(true);
    
    const q = query(
      collection(db, 'tooltipPackages'),
      where('creatorId', '==', user?.uid),
    );
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      try {
        // Extract all package IDs first
        const packageIds: string[] = [];
        const packageDocsMap = new Map<string, TooltipPackageDoc>();
        
        querySnapshot.docs.forEach((doc) => {
          const data = doc.data() as TooltipPackageDoc;
          const packageId = data.packageId || doc.id;
          packageIds.push(packageId);
          packageDocsMap.set(packageId, data);
        });

        // Fetch ALL purchase data in batches (much faster than individual queries)
        const purchaseDataMap = await getPurchaseDataForPackages(packageIds);

        // Now process all packages with pre-fetched purchase data
        const packages: TooltipPackage[] = [];
        
        for (const packageId of packageIds) {
          const data = packageDocsMap.get(packageId);
          if (!data) continue;

          const purchases = purchaseDataMap.get(packageId) || [];
          const stats = computePackageStats(purchases);

          const priceNumber = Number(data.price) || 0;

          // parse createdAt into a JS Date and a raw timestamp for sorting
          let createdDisplay = 'N/A';
          let createdRaw: number | undefined = undefined;
          
          const createdDate = parseFirestoreTimestamp(data.createdAt);
          if (createdDate) {
            createdDisplay = createdDate.toLocaleDateString();
            createdRaw = createdDate.getTime();
          }

          packages.push({
            packageId: packageId,
            packageName: data.name || 'Unnamed Package',
            created: createdDisplay,
            createdAtRaw: createdRaw,
            lastSale: stats.lastSale,
            ratings: stats.averageRating,
            sales: stats.salesCount,
            earnings: stats.totalEarnings.toFixed(2),
            status: data.status === 'published',
            price: priceNumber,
            currency: data.currency || 'USD',
            primaryUrl: data.primaryUrl || '',
            tooltips: data.tooltips || [],
            admindisabled: data.admindisabled || false,
            description: data.description
          });
        }
        
        // Sort packages by createdAtRaw descending (most recent first)
        packages.sort((a, b) => {
          const ta = a.createdAtRaw ?? 0;
          const tb = b.createdAtRaw ?? 0;
          return tb - ta;
        });

        setTooltipPackages(packages);
        setFetchingPackages(false);
      } catch (error) {
        console.error('Error processing tooltip packages:', error);
        setFetchingPackages(false);
      }
    }, (error) => {
      console.error('Error in packages listener:', error);
      setFetchingPackages(false);
    });
    
    return unsubscribe;
  }, [user?.uid]);

  // Real-time listener for tooltip sales data using onSnapshot
  const setupSalesListener = useCallback(() => {
    if (!user?.uid) return;
    
    setFetchingSales(true);
    
    // Step 1: First get all packages created by this expert
    const packagesQuery = query(
      collection(db, 'tooltipPackages'),
      where('creatorId', '==', user.uid)
    );
    
    // Store unsubscribe functions
    const unsubscribes: (() => void)[] = [];
    
    // Listen to packages
    const packagesUnsubscribe = onSnapshot(packagesQuery, async (packagesSnapshot) => {
      try {
  const packageDataMap = new Map<string, { name: string; price: number; primaryUrl?: string }>();
        const packageIds: string[] = [];
        
        packagesSnapshot.docs.forEach((packageDoc) => {
          const packageData = packageDoc.data();
          const packageId = packageData.packageId || packageDoc.id;
          packageIds.push(packageId);
          packageDataMap.set(packageId, {
            name: packageData.name || 'Unnamed Package',
            price: packageData.price || 0,
            primaryUrl: packageData.primaryUrl || ''
          });
        });

        if (packageIds.length === 0) {
          setTooltipSalesData([]);
          setFetchingSales(false);
          return;
        }

        // Step 2: Fetch ALL purchases for these packages in batches
        const purchaseDataMap = await getPurchaseDataForPackages(packageIds);
        
        // Step 3: Get unique purchaser IDs
        const purchaserIds = new Set<string>();
        purchaseDataMap.forEach((purchases) => {
          purchases.forEach((purchase: TooltipPurchase) => {
            if (purchase.purchaserId) {
              purchaserIds.add(purchase.purchaserId);
            }
          });
        });

        // Step 4+: Real-time name & org resolution using onSnapshot
        // We'll listen to profiles queries in batches, then listen to missing user docs
        // and organizations docs so updates to names/org names reflag automatically.

        const customerNamesMap = new Map<string, string>();
        const profileNamesMap = new Map<string, string>();
        const purchaserIdsArray = Array.from(purchaserIds);
        const profileBatchSize = 10;

        // Keep maps for user -> orgId and orgId -> orgName
        const purchaserOrgIdMap = new Map<string, string>();
        const orgNamesMap = new Map<string, string>();

        // Helper to (re)build sales data from current maps
        const buildAndSetSalesData = () => {
          const salesData: TooltipSalesData[] = [];

          purchaseDataMap.forEach((purchases, packageId) => {
            const packageInfo = packageDataMap.get(packageId);
            if (!packageInfo) return;

            purchases.forEach((purchase: TooltipPurchase) => {
              const purchaserId = purchase.purchaserId || '';
              const customerName = profileNamesMap.get(purchaserId) || 'Unknown Customer';
              const profileName = customerName;
              const orgId = purchaserOrgIdMap.get(purchaserId) || '';
              const orgName = orgNamesMap.get(orgId) || '';

              const purchaseDate = parseFirestoreTimestamp(purchase.purchasedAt) || new Date();
              const formattedPurchaseDate = purchaseDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              });

              salesData.push({
                packageId: packageId,
                packageName: packageInfo.name,
                customerName: profileName,
                purchased: formattedPurchaseDate,
                purchasedAt: purchaseDate.toISOString(),
                ratings: typeof purchase.rating === 'number' ? purchase.rating : Number(purchase.rating) || 0,
                reviews: purchase.review || 'No review',
                earnings: packageInfo.price.toFixed(2),
                price: packageInfo.price,
                orgName: orgName,
                primaryUrl: packageInfo.primaryUrl || '',
              } as TooltipSalesData);
            });
          });

          // Sort by raw purchasedAt timestamp (most recent first)
          salesData.sort((a, b) => {
            const timeA = a.purchasedAt ? new Date(a.purchasedAt).getTime() : 0;
            const timeB = b.purchasedAt ? new Date(b.purchasedAt).getTime() : 0;
            return timeB - timeA;
          });

          // Collect unique organizations
          const orgSet = new Set<string>();
          salesData.forEach((sale) => {
            if (sale.orgName) {
              orgSet.add(sale.orgName);
            }
          });
          const uniqueOrgOptions = Array.from(orgSet).map((org) => ({
            value: org,
            label: org,
          }));
          setUniqueOrgs(uniqueOrgOptions);

          setTooltipSalesData(salesData);
        };

        // Collect unsubscribe functions so they can be cleaned up
        const snapshotUnsubscribes: (() => void)[] = [];

        // 1) Listen to profiles in batches (profiles collection keyed by userId)
        for (let i = 0; i < purchaserIdsArray.length; i += profileBatchSize) {
          const batch = purchaserIdsArray.slice(i, i + profileBatchSize);
          const profilesQuery = query(
            collection(db, 'profiles'),
            where('userId', 'in', batch)
          );

          const profilesUnsub = onSnapshot(profilesQuery, (profilesSnapshot) => {
            profilesSnapshot.docs.forEach((docSnap) => {
              const profileData = docSnap.data() as Record<string, unknown>;
              // profile docs may store the user id in a field like 'userId' or use the document id
              const profileUserId = (profileData['userId'] as string | undefined) || docSnap.id;
              const profileName = profileData['name'] as string | undefined;
              console.log(profileName,"profileName")
              if (profileUserId && profileName) {
                // Map purchaserId (which equals userId) to the profile name
                profileNamesMap.set(profileUserId, profileName);
              }
            });
            // rebuild sales data when profiles update
            buildAndSetSalesData();
          }, (err) => {
            console.error('Profiles snapshot error:', err);
          });

          snapshotUnsubscribes.push(profilesUnsub);
        }

        // 2) For any purchaser IDs we still don't have names for, listen to their user docs (contains orgId and fallback name)
        const missingUserIds = purchaserIdsArray.filter(id => !customerNamesMap.has(id));
        const listenedOrgIds = new Set<string>();

        for (const userId of missingUserIds) {
          try {
            const userDocRef = doc(db, 'users', userId);
            const userUnsub = onSnapshot(userDocRef, (userDoc) => {
              if (!userDoc.exists()) return;
                const userData = userDoc.data() as Record<string, unknown>;
                if (userData) {
                  const name = userData['name'] as string | undefined;
                  const orgId = userData['orgId'] as string | undefined;
                  if (name) {
                    customerNamesMap.set(userId, name);
                  }
                  if (orgId) {
                    purchaserOrgIdMap.set(userId, orgId);

                    // start listening to organization name if not already
                    if (!listenedOrgIds.has(orgId)) {
                      listenedOrgIds.add(orgId);
                      const orgRef = doc(db, 'organizations', orgId);
                      const orgUnsub = onSnapshot(orgRef, (orgDoc) => {
                        if (!orgDoc.exists()) return;
                        const orgData = orgDoc.data() as Record<string, unknown>;
                        const orgName = orgData['name'] as string | undefined;
                        if (orgName) {
                          orgNamesMap.set(orgId, orgName);
                        }
                        // rebuild sales data when organization name changes
                        buildAndSetSalesData();
                      }, (err) => {
                        console.error(`Organization snapshot error for ${orgId}:`, err);
                      });

                      snapshotUnsubscribes.push(orgUnsub);
                    }
                  }
                }

              // rebuild sales data whenever a user doc updates (name/orgId)
              buildAndSetSalesData();
            }, (err) => {
              console.error(`User snapshot error for ${userId}:`, err);
            });

            snapshotUnsubscribes.push(userUnsub);
          } catch (error) {
            console.error('Error setting up user snapshot for', userId, error);
          }
        }

        // 3) For purchaserIds we got names for from profiles, we still need their orgId -> attempt to listen to their user doc to get orgId and then organization name
        const profileProvidedIds = purchaserIdsArray.filter(id => customerNamesMap.has(id));
        for (const userId of profileProvidedIds) {
          try {
            const userDocRef = doc(db, 'users', userId);
            const userUnsub = onSnapshot(userDocRef, (userDoc) => {
              if (!userDoc.exists()) return;
              const userData = userDoc.data() as Record<string, unknown>;
              const orgId = userData['orgId'] as string | undefined;
              if (orgId) {
                purchaserOrgIdMap.set(userId, orgId);
                if (orgId && !listenedOrgIds.has(orgId)) {
                  listenedOrgIds.add(orgId);
                  const orgRef = doc(db, 'organizations', orgId);
                  const orgUnsub = onSnapshot(orgRef, (orgDoc) => {
                    if (!orgDoc.exists()) return;
                    const orgData = orgDoc.data() as Record<string, unknown>;
                    const orgName = orgData['name'] as string | undefined;
                    if (orgName) {
                      orgNamesMap.set(orgId, orgName);
                    }
                    buildAndSetSalesData();
                  }, (err) => {
                    console.error(`Organization snapshot error for ${orgId}:`, err);
                  });

                  snapshotUnsubscribes.push(orgUnsub);
                }
              }
              buildAndSetSalesData();
            }, (err) => {
              console.error(`User snapshot error for ${userId}:`, err);
            });

            snapshotUnsubscribes.push(userUnsub);
          } catch (error) {
            console.error('Error setting up user snapshot for', userId, error);
          }
        }

        // Add our new snapshot unsubs to the outer unsubscribes so the parent cleanup removes them
        unsubscribes.push(...snapshotUnsubscribes);

        // Initial build (in case no snapshots fired yet but we have some data)
        buildAndSetSalesData();
        setFetchingSales(false);
      } catch (error) {
        console.error('Error processing tooltip sales data:', error);
        setFetchingSales(false);
      }
    }, (error) => {
      console.error('Error in sales listener:', error);
      setFetchingSales(false);
    });
    
    unsubscribes.push(packagesUnsubscribe);
    
    // Return cleanup function that unsubscribes from all listeners
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [user?.uid]);

  // Create tooltip in Firestore
  const createTooltipInDatabase = async (packageId: string, packageData: { name: string; primaryUrl: string; price: string; description?: string }) => {
    try {
      console.log("Creating tooltip in database with ID:", packageId);

      const tooltipRef = doc(db, `tooltipPackages/${packageId}`);
      const tooltipPackages = {
        packageId: packageId,
        creatorId: user?.uid,
        name: packageData?.name || "New Tooltip Package",
        // Store normalized, fully-qualified URL (force https and add www for bare domains)
        primaryUrl: normalizeUrl(packageData?.primaryUrl) || "",
        price: parseFloat(packageData?.price) || 0,
       description: packageData?.description || "",
        createdAt: new Date().toISOString(),
        status: "draft",
        delete: Boolean(false),
        currency: "USD",
        admindisabled: Boolean(false),
        tooltips: [],
        purchasers: [],
        version: 1,

      };

      await setDoc(tooltipRef, tooltipPackages);
      console.log("Tooltip created successfully:", packageId);

      // Add packageId to the user's createdPackages array on their user document
      try {
        if (user?.uid) {
          const userRef = doc(db, `users/${user.uid}`);
          // Try to update using arrayUnion which will create the array if it doesn't exist
          await updateDoc(userRef, {
            createdPackages: arrayUnion(packageId),
          });
        }
      } catch (err) {
        console.error('Error updating user createdPackages:', err);
        // If updateDoc fails because the document doesn't exist, create the document with the field
        try {
          if (user?.uid) {
            const userRef = doc(db, `users/${user.uid}`);
            await setDoc(userRef, { createdPackages: [packageId] }, { merge: true });
          }
        } catch (e) {
          console.error('Failed to add packageId to user document:', e);
        }
      }

      // Verify the data was written successfully
      const snapshot = await getDoc(tooltipRef);
      if (snapshot.exists()) {
        console.log("Verified tooltip in database:", snapshot.data());
      } else {
        console.error("Tooltip data not found in database after write.");
      }
    } catch (error) {
      console.error("Error creating tooltip in Firestore:", error);
      throw error;
    }
  };

  // Update tooltip in Firestore
  const updateTooltipInDatabase = async (packageId: string, packageData: { name: string; primaryUrl: string; price: string; description?: string }) => {
    try {
      console.log("Updating tooltip in database with ID:", packageId);

      const tooltipRef = doc(db, `tooltipPackages/${packageId}`);
      const updateData = {
        name: packageData?.name || "Updated Tooltip Package",
        // Normalize URL before saving
        primaryUrl: normalizeUrl(packageData?.primaryUrl) || "",
        price: parseFloat(packageData?.price) || 0,
        description: packageData?.description || "",
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(tooltipRef, updateData);
      console.log("Tooltip updated successfully:", packageId);

      // Verify the data was updated successfully
      const snapshot = await getDoc(tooltipRef);
      if (snapshot.exists()) {
        console.log("Verified updated tooltip in database:", snapshot.data());
      } else {
        console.error("Tooltip data not found in database after update.");
      }
    } catch (error) {
      console.error("Error updating tooltip in Firestore:", error);
      throw error;
    }
  };

    // Check if extension is installed and user is logged in
    const checkExtensionLogin = async (): Promise<boolean> => {
      return new Promise((resolve) => {
        try {
          if (window.chrome && window.chrome.runtime) {
            window.chrome.runtime.sendMessage(
              "hedjogelnmjoilmlomnekfbchmbfngja",
              { type: "CHECK_LOGIN" },
              (response) => {
                if (chrome.runtime.lastError) {
                  resolve(false);
                } else {
                  resolve(response?.success ?? false);
                }
              }
            );
          } else {
            resolve(false);
          }
        } catch (e) {
          console.error('Extension check error:', e);
          resolve(false);
        }
      });
    };

    // Reusable sendTooltipRequest with retry and fallback (postMessage)
    const sendTooltipRequest = async (packageId: string, action: string, retries = 3, tooltip_type?: string): Promise<boolean> => {
      for (let i = 0; i < retries; i++) {
        try {
          if (window.chrome && window.chrome.runtime) {
            const response = await new Promise<{ success?: boolean; error?: string }>((resolve, reject) => {
              window.chrome.runtime.sendMessage(
                "hedjogelnmjoilmlomnekfbchmbfngja",
                {
                  type: "INJECT_TOOLTIP_REQUEST",
                  payload: {
                    packageId,
                    userId: user?.uid,
                    action,
                    tooltip_type,
                  },
                },
                (resp: { success?: boolean; error?: string }) => {
                  if (chrome.runtime.lastError) {
                    reject(new Error(chrome.runtime.lastError.message));
                  } else {
                    resolve(resp || {});
                  }
                }
              );
            });

            if (response && response.success) {
              return true;
            } else {
              console.log(`Injection attempt ${i + 1} failed:`, response);
              if (i < retries - 1) await new Promise(r => setTimeout(r, 1000));
            }
          } else {
            // Fallback for non-extension environments
            window.postMessage(
              {
                type: "TOOLTIP_INJECTION_REQUEST",
                source: "supporty_web",
                payload: { packageId, userId: user?.uid, action, tooltip_type },
              },
              "*"
            );
            return true;
          }
        } catch (err) {
          console.log(`Injection attempt ${i + 1} error:`, err);
          if (i < retries - 1) await new Promise(r => setTimeout(r, 1000));
        }
      }
      return false;
    };

    // Check if tooltips are injected in current tab
    const checkTooltipInjectionStatus = async (): Promise<{ isInjected: boolean; hasData: boolean }> => {
      return new Promise((resolve) => {
        try {
          if (window.chrome && window.chrome.runtime) {
            window.chrome.runtime.sendMessage(
              "hedjogelnmjoilmlomnekfbchmbfngja",
              { type: "CHECK_TOOLTIP_INJECTION_STATUS" },
              (response) => {
                if (chrome.runtime.lastError) {
                  resolve({ isInjected: false, hasData: false });
                } else {
                  resolve({
                    isInjected: response?.isInjected ?? false,
                    hasData: response?.hasData ?? false
                  });
                }
              }
            );
          } else {
            resolve({ isInjected: false, hasData: false });
          }
        } catch (e) {
          console.error('Error checking tooltip injection status:', e);
          resolve({ isInjected: false, hasData: false });
        }
      });
    };

    // Force re-injection of tooltips for current tab
    const forceReinjectTooltips = async (): Promise<boolean> => {
      return new Promise((resolve) => {
        try {
          if (window.chrome && window.chrome.runtime) {
            window.chrome.runtime.sendMessage(
              "hedjogelnmjoilmlomnekfbchmbfngja",
              { type: "FORCE_REINJECT_TOOLTIP" },
              (response) => {
                if (chrome.runtime.lastError) {
                  console.error('Force reinject error:', chrome.runtime.lastError);
                  resolve(false);
                } else {
                  resolve(response?.success ?? false);
                }
              }
            );
          } else {
            resolve(false);
          }
        } catch (e) {
          console.error('Error forcing tooltip re-injection:', e);
          resolve(false);
        }
      });
    };

    // Get tooltip data for current tab
    const getTabTooltipData = async (): Promise<any> => {
      return new Promise((resolve) => {
        try {
          if (window.chrome && window.chrome.runtime) {
            window.chrome.runtime.sendMessage(
              "hedjogelnmjoilmlomnekfbchmbfngja",
              { type: "GET_TAB_TOOLTIP_DATA" },
              (response) => {
                if (chrome.runtime.lastError) {
                  resolve(null);
                } else {
                  resolve(response?.data ?? null);
                }
              }
            );
          } else {
            resolve(null);
          }
        } catch (e) {
          console.error('Error getting tab tooltip data:', e);
          resolve(null);
        }
      });
    };

    // Clear tooltip data for current tab
    const clearTabTooltipData = async (): Promise<boolean> => {
      return new Promise((resolve) => {
        try {
          if (window.chrome && window.chrome.runtime) {
            window.chrome.runtime.sendMessage(
              "hedjogelnmjoilmlomnekfbchmbfngja",
              { type: "CLEAR_TAB_TOOLTIP_DATA" },
              (response) => {
                if (chrome.runtime.lastError) {
                  resolve(false);
                } else {
                  resolve(response?.success ?? false);
                }
              }
            );
          } else {
            resolve(false);
          }
        } catch (e) {
          console.error('Error clearing tab tooltip data:', e);
          resolve(false);
        }
      });
    };

    // Handler for "Edit Tooltips" button - navigate to primaryUrl, check extension and send edit request
    const handleEditTooltips = async () => {
      if (!editingPackageId) return;
      setIsLoading(true);
      setErrorMessage('');

      try {
        // Ensure primaryUrl is present
        if (!formData.primaryUrl) {
          setErrorMessage('Primary URL is missing for this package.');
          setIsLoading(false);
          return;
        }

        const extensionOk = await checkExtensionLogin();
        if (!extensionOk) {
          setErrorMessage('Please ensure the extension is installed and you are logged in.');
          setIsLoading(false);
          return;
        }

        // Open primary URL in a new tab (use normalized URL)
        const normalized = normalizeUrl(formData.primaryUrl);
        window.open(normalized, '_blank');

        // Give the page a moment to load
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Send injection request to extension to start edit flow with tooltip_type
        const success = await sendTooltipRequest(editingPackageId, 'edit_package', 3, 'editer');
        if (!success) {
          console.warn('Failed to send edit injection to extension after retries');
          // don't fail the edit operation - just inform via console and let user continue
        }

        // Optionally, keep the modal open so the user can continue editing in UI, or close it
        // We'll close it to mirror the create flow
        setCreateTooltipModel(false);
      } catch (err) {
        console.error('Error handling edit tooltips:', err);
        setErrorMessage('Error initiating edit flow.');
      } finally {
        setIsLoading(false);
      }
    };

  const handleNext = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setErrorMessage("");

    // Check if all fields are filled (including description)
    // Treat scheme-only values as empty (e.g., 'https://')
    const primaryUrlTrimmed = formData.primaryUrl ? formData.primaryUrl.trim() : '';
    const primaryUrlIsBareScheme = primaryUrlTrimmed === 'https://' || primaryUrlTrimmed === 'http://';

    if (!formData.name || !formData.primaryUrl || primaryUrlIsBareScheme || !formData.price || !formData.description) {
      setErrorMessage("All fields are required.");
      setIsLoading(false);
      return;
    }

    // Check for URL validation error
    if (urlError) {
      setIsLoading(false);
      return;
    }

    try {
      console.log("Form data:", formData);

      if (isEditMode && editingPackageId) {
        // Update existing tooltip package
        await updateTooltipInDatabase(editingPackageId, formData);
        
        setCreateTooltipModel(false);
        setFormData({
          name: '',
          primaryUrl: '',
          price: '',
          description: ''
        });
        setIsEditMode(false);
        setEditingPackageId(null);
        
        // Real-time listeners will automatically update the UI
        setIsLoading(false);
        return;
      }

      // Check if extension is installed and user is logged in for new packages
      const extensionCheck = await new Promise((resolve) => {
        if (window.chrome && window.chrome.runtime) {
          window.chrome.runtime.sendMessage(
            "hedjogelnmjoilmlomnekfbchmbfngja", // Your extension ID
            { type: "CHECK_LOGIN" },
            (response) => {
              if (chrome.runtime.lastError) {
                resolve(false);
              } else {
                resolve(response?.success);
              }
            }
          );
        } else {
          resolve(false);
        }
      });

      if (!extensionCheck) {
        setErrorMessage("Please ensure the extension is installed and you are logged in.");
        setIsLoading(false);
        return;
      }

      // Generate tooltip ID and create in database
      const packageId = generateTooltipId();
      await createTooltipInDatabase(packageId, formData);
      localStorage.setItem("packageId", packageId);

      // Send message to extension to inject tooltip scripts
      try {
        // Open the provided primary URL in a new tab (use normalized form)
        if (formData.primaryUrl) {
          const normalized = normalizeUrl(formData.primaryUrl);
          window.open(normalized, "_blank");
        }

        // Wait a bit for the tab to load before sending the injection request
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Send message to extension with response handling and retry logic
        const sendTooltipRequest = async (retries = 3): Promise<boolean> => {
          for (let i = 0; i < retries; i++) {
            try {
              if (window.chrome && window.chrome.runtime) {
                const response = await new Promise<{ success?: boolean; error?: string }>((resolve, reject) => {
                  window.chrome.runtime.sendMessage(
                    "hedjogelnmjoilmlomnekfbchmbfngja", // Your extension ID
                    {
                      type: "INJECT_TOOLTIP_REQUEST",
                      payload: {
                        packageId: packageId,
                        userId: user?.uid,
                        action: "create_package",
                        tooltip_type: "create",
                      },
                    },
                    (response: { success?: boolean; error?: string }) => {
                      if (chrome.runtime.lastError) {
                        reject(new Error(chrome.runtime.lastError.message));
                      } else {
                        resolve(response || {});
                      }
                    }
                  );
                });

                if (response && response.success) {
                  console.log("Tooltip injection successful");
                  return true;
                } else {
                  console.log(`Injection attempt ${i + 1} failed:`, response);
                  if (i < retries - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                  }
                }
              } else {
                // Fallback method
                window.postMessage(
                  {
                    type: "TOOLTIP_INJECTION_REQUEST",
                    source: "supporty_web",
                    payload: {
                      packageId: packageId,
                      userId: user?.uid,
                      action: "create_package",
                    },
                  },
                  "*"
                );
                return true;
              }
            } catch (error) {
              console.log(`Injection attempt ${i + 1} error:`, error);
              if (i < retries - 1) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
          }
          return false;
        };

        const success = await sendTooltipRequest();
        if (!success) {
          console.warn("Failed to inject tooltip after retries");
          // Don't show error to user as the package was created successfully
        }

        // Additional debugging: Check if localStorage can be accessed on the current page
        try {
          localStorage.setItem("debug_test", "test");
          const testResult = localStorage.getItem("debug_test");
          localStorage.removeItem("debug_test");
          console.log("Local localStorage test successful:", testResult === "test");
        } catch (debugError) {
          console.error("Local localStorage test failed:", debugError);
        }

      } catch (error) {
        console.error("Error sending message to extension:", error);
        // Don't show error to user as the package was created successfully
      }

      setCreateTooltipModel(false);
      setFormData({
        name: '',
        primaryUrl: '',
        price: '',
        description: ''
      });
      
      // Real-time listeners will automatically update the UI
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} tooltip:`, error);
      setErrorMessage(`Error ${isEditMode ? 'updating' : 'creating'} tooltip in database!`);
    } finally {
      setIsLoading(false);
    }
  };



  // Add tooltip type change handler
  const handleTooltipTypeChange = (types: string[]) => {
    setSelectedTooltipTypes(types || []);
    // Reset sales pagination when filter changes
    setSalesPage(1);
  };

  // Sales filter handlers
  const handleOrganizationChange = (orgs: string[]) => {
    setSelectedOrganizations(orgs || []);
    setSalesPage(1);
  };

  const handleDateRangeChange = (range: { from: string; to: string }) => {
    setDateFrom(range.from);
    setDateTo(range.to);
    setSalesPage(1);
  };

  const handleRatingChange = (rating: string) => {
    setSelectedRating(rating);
    setSalesPage(1);
  };

  // Select data and pagination state based on activeTab
  // Apply search filtering to packages and sales
  const lowerQuery = searchQuery.trim().toLowerCase();
  const filteredPackages = tooltipPackages.filter((p) => {
    if (!lowerQuery) return true;
    return (
      (p.packageName || "").toLowerCase().includes(lowerQuery) ||
      (p.primaryUrl || "").toLowerCase().includes(lowerQuery) ||
      (p.description || "").toLowerCase().includes(lowerQuery)
    );
  }).filter((p) => {
    // Filter by selected packages
    if (selectedTooltipTypes && selectedTooltipTypes.length > 0) {
      return selectedTooltipTypes.includes(p.packageId);
    }
    return true;
  }).filter((p) => {
    // Filter by package tooltip rating range
    if (appliedFilters?.packageTooltipRating) {
      const selected = parseFloat(appliedFilters.packageTooltipRating);
      const min = selected - 0.4;
      const max = selected + 0.5;
      return p.ratings >= min && p.ratings <= max;
    }
    return true;
  }).filter((p) => {
    // Filter by sales ranges
    if (appliedFilters?.salesRanges) {
      const sales = p.sales || 0;
      const ranges = appliedFilters.salesRanges;
      let matches = false;

      if (ranges.range1 && sales >= 0 && sales <= 10) matches = true;
      if (ranges.range2 && sales >= 11 && sales <= 20) matches = true;
      if (ranges.range3 && sales >= 21 && sales <= 50) matches = true;
      if (ranges.range4 && sales >= 51 && sales <= 100) matches = true;
      if (ranges.range5 && sales >= 200) matches = true;
      if (ranges.custom && ranges.customMin !== null && ranges.customMax !== null) {
        if (sales >= ranges.customMin && sales <= ranges.customMax) matches = true;
      } else if (ranges.custom && ranges.customMin !== null) {
        if (sales >= ranges.customMin) matches = true;
      } else if (ranges.custom && ranges.customMax !== null) {
        if (sales <= ranges.customMax) matches = true;
      }

      // If any range is selected but doesn't match, filter out
      const hasAnyRange = ranges.range1 || ranges.range2 || ranges.range3 || ranges.range4 || ranges.range5 || ranges.custom;
      if (hasAnyRange && !matches) return false;
    }
    return true;
  }).filter((p) => {
    // Filter by earnings ranges
    if (appliedFilters?.earningsRanges) {
      const earningsStr = p.earnings || "$0";
      const earnings = parseFloat(earningsStr.replace(/[$,]/g, '')) || 0;
      const ranges = appliedFilters.earningsRanges;
      let matches = false;

      if (ranges.range1 && earnings >= 0 && earnings <= 100) matches = true;
      if (ranges.range2 && earnings >= 101 && earnings <= 200) matches = true;
      if (ranges.range3 && earnings >= 201 && earnings <= 300) matches = true;
      if (ranges.range4 && earnings >= 301 && earnings <= 400) matches = true;
      if (ranges.range5 && earnings >= 401 && earnings <= 500) matches = true;
      if (ranges.range6 && earnings >= 501) matches = true;
      if (ranges.custom && ranges.customMin !== null && ranges.customMax !== null) {
        if (earnings >= ranges.customMin && earnings <= ranges.customMax) matches = true;
      } else if (ranges.custom && ranges.customMin !== null) {
        if (earnings >= ranges.customMin) matches = true;
      } else if (ranges.custom && ranges.customMax !== null) {
        if (earnings <= ranges.customMax) matches = true;
      }

      // If any range is selected but doesn't match, filter out
      const hasAnyRange = ranges.range1 || ranges.range2 || ranges.range3 || ranges.range4 || ranges.range5 || ranges.range6 || ranges.custom;
      if (hasAnyRange && !matches) return false;
    }
    return true;
  }).filter((p) => {
    // Filter by created date range
    if (appliedFilters?.createdDateRange) {
      let createdDate: Date | null = null;
      if (p.createdAtRaw) {
        createdDate = new Date(p.createdAtRaw);
      } else if (p.created && p.created !== 'N/A') {
        const dateParts = p.created.split('/');
        if (dateParts.length === 3) {
          const month = parseInt(dateParts[0], 10) - 1;
          const day = parseInt(dateParts[1], 10);
          const year = parseInt(dateParts[2], 10);
          createdDate = new Date(year, month, day);
        }
      }
      if (!createdDate || isNaN(createdDate.getTime())) return false;
      const createdDateOnly = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
      if (appliedFilters.createdDateRange.from) {
        const fromDate = new Date(appliedFilters.createdDateRange.from);
        const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
        if (createdDateOnly < fromDateOnly) return false;
      }
      if (appliedFilters.createdDateRange.to) {
        const toDate = new Date(appliedFilters.createdDateRange.to);
        const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
        if (createdDateOnly > toDateOnly) return false;
      }
    }
    return true;
  }).filter((p) => {
    // Filter by last sale date range
    if (appliedFilters?.lastSaleDateRange) {
      if (p.lastSale === 'N/A') return false; // Exclude packages with no sales when filter is applied
      const dateParts = p.lastSale.split('/');
      if (dateParts.length !== 3) return false;
      const month = parseInt(dateParts[0], 10) - 1;
      const day = parseInt(dateParts[1], 10);
      const year = parseInt(dateParts[2], 10);
      const lastSaleDate = new Date(year, month, day);
      if (isNaN(lastSaleDate.getTime())) return false;
      const lastSaleDateOnly = new Date(lastSaleDate.getFullYear(), lastSaleDate.getMonth(), lastSaleDate.getDate());
      if (appliedFilters.lastSaleDateRange.from) {
        const fromDate = new Date(appliedFilters.lastSaleDateRange.from);
        const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
        if (lastSaleDateOnly < fromDateOnly) return false;
      }
      if (appliedFilters.lastSaleDateRange.to) {
        const toDate = new Date(appliedFilters.lastSaleDateRange.to);
        const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
        if (lastSaleDateOnly > toDateOnly) return false;
      }
    }
    return true;
  });

  // Apply sorting to packages if requested (sales or earnings)
  // Memoized sorted packages (mirrors MyTooltip implementation)
  const sortedPackages = React.useMemo(() => {
  const sorted = [...filteredPackages];
    if (sortColumn) {
      sorted.sort((a, b) => {
        // Name sorting (string) — use localeCompare for correct alphabetical order
        if (sortColumn === 'name') {
          const aName = (a.packageName || '').toString().toLowerCase();
          const bName = (b.packageName || '').toString().toLowerCase();
          const cmp = aName.localeCompare(bName);
          return sortDirection === 'asc' ? cmp : -cmp;
        }

        let aVal = 0;
        let bVal = 0;
        if (sortColumn === 'sales') {
          aVal = a.sales || 0;
          bVal = b.sales || 0;
        } else if (sortColumn === 'earnings') {
          aVal = parseFloat(String(a.earnings || '0')) || 0;
          bVal = parseFloat(String(b.earnings || '0')) || 0;
        } else {
          return 0;
        }
        if (sortDirection === 'asc') {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      });
    }
    return sorted;
  }, [filteredPackages, sortColumn, sortDirection]);

  

  const filteredSales = tooltipSalesData.filter((s) => {
    if (!lowerQuery) return true;
    return (
      (s.packageName || "").toLowerCase().includes(lowerQuery) ||
      (s.customerName || "").toLowerCase().includes(lowerQuery) ||
      (s.primaryUrl || "").toLowerCase().includes(lowerQuery)
    );
  }).filter((s) => {
    // Filter by selected packages
    if (selectedTooltipTypes && selectedTooltipTypes.length > 0) {
      return selectedTooltipTypes.includes(s.packageId);
    }
    return true;
  }).filter((s) => {
    // Filter by organizations
    if (selectedOrganizations && selectedOrganizations.length > 0) {
      return selectedOrganizations.includes(s.orgName);
    }
    return true;
  }).filter((s) => {
    // Filter by rating
    if (selectedRating && selectedRating !== "") {
      return s.ratings === parseInt(selectedRating);
    }
    return true;
  }).filter((s) => {
    // Filter by date range
    if (dateFrom || dateTo) {
      const purchaseDate = parseFirestoreTimestamp(s.purchasedAt);
      if (!purchaseDate) return false;
      
      // Normalize dates to compare only date parts (ignore time)
      const purchaseDateOnly = new Date(purchaseDate.getFullYear(), purchaseDate.getMonth(), purchaseDate.getDate());
      
      if (dateFrom) {
        const fromDate = new Date(dateFrom + 'T00:00:00');
        const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
        if (purchaseDateOnly < fromDateOnly) return false;
      }
      if (dateTo) {
        const toDate = new Date(dateTo + 'T23:59:59');
        const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
        if (purchaseDateOnly > toDateOnly) return false;
      }
    }
    return true;
  });

  // Apply sorting to sales data if requested (earnings)
  const sortedSales = React.useMemo(() => {
    const sorted: TooltipSalesData[] = [...filteredSales];
    if (sortColumn) {
      if (sortColumn === 'earnings') {
        sorted.sort((a: TooltipSalesData, b: TooltipSalesData) => {
          const aVal = parseFloat(String(a.earnings || '0')) || 0;
          const bVal = parseFloat(String(b.earnings || '0')) || 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });
      } else if (sortColumn === 'name') {
        sorted.sort((a: TooltipSalesData, b: TooltipSalesData) => {
          const aName = (a.packageName || '').toString().toLowerCase();
          const bName = (b.packageName || '').toString().toLowerCase();
          const cmp = aName.localeCompare(bName);
          return sortDirection === 'asc' ? cmp : -cmp;
        });
      }
    }
    return sorted;
  }, [filteredSales, sortColumn, sortDirection]);

  const filterDate = activeTab === "package" ? sortedPackages : sortedSales;
  const currentPage = activeTab === "package" ? packagePage : salesPage;
  const setCurrentPage = activeTab === "package" ? setPackagePage : setSalesPage;

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filterDate.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filterDate.length / itemsPerPage);

  // Handle page change for the current tab
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // When switching tabs, reset the corresponding page to 1
  const handleTabChange = (tab: string) => {
    // Switch active tab
    setActiveTab(tab);

    // Reset pagination for both tabs so each tab always starts at page 1 when visited
    setPackagePage(1);
    setSalesPage(1);

    // Reset search and selected tooltip types so each tab shows an unfiltered list by default
    setSearchQuery('');
    setSelectedTooltipTypes([]);

    // Clear applied package filters (created date, sales ranges, earnings ranges, etc.)
    setAppliedFilters(null);

    // Reset sales-specific filters
    setSelectedOrganizations([]);
    setDateFrom('');
    setDateTo('');
    setSelectedRating('');

    // Reset sorting so each tab has default ordering
    setSortColumn(null);
    setSortDirection('asc');
  };

  // Open create modal when the page is visited with ?create=true
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("create") === "true") {
        setCreateTooltipModel(true);
        setFormData(prev => ({ ...prev, primaryUrl: 'https://' }));

        // Remove the query param without reloading the page
        params.delete("create");
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
      }
    } catch {
      // ignore
    }
  }, []);

  // Check tooltip injection status on component mount
  React.useEffect(() => {
    const checkInjectionStatus = async () => {
      setTooltipInjectionStatus(prev => ({ ...prev, isChecking: true }));
      try {
        const status = await checkTooltipInjectionStatus();
        setTooltipInjectionStatus({
          isInjected: status.isInjected,
          hasData: status.hasData,
          isChecking: false
        });
      } catch (error) {
        console.error('Error checking tooltip injection status:', error);
        setTooltipInjectionStatus({
          isInjected: false,
          hasData: false,
          isChecking: false
        });
      }
    };

    // Check status when component mounts
    checkInjectionStatus();

    // Set up periodic status checking (every 30 seconds)
    const statusInterval = setInterval(checkInjectionStatus, 30000);

    return () => {
      clearInterval(statusInterval);
    };
  }, []);

  // Handle manual re-injection
  const handleReinjectTooltips = async () => {
    setIsReinjecting(true);
    try {
      const success = await forceReinjectTooltips();
      if (success) {
        // Refresh status after re-injection
        setTimeout(async () => {
          const status = await checkTooltipInjectionStatus();
          setTooltipInjectionStatus({
            isInjected: status.isInjected,
            hasData: status.hasData,
            isChecking: false
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error during manual re-injection:', error);
    } finally {
      setIsReinjecting(false);
    }
  };

  // Set up real-time listener for tooltip packages when user is available
  React.useEffect(() => {
    if (user?.uid) {
      const unsubscribe = setupPackagesListener();
      // Cleanup function to unsubscribe when component unmounts or user changes
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [user?.uid, setupPackagesListener]);

  // Set up real-time listener for tooltip sales data when user is available
  useEffect(() => {
    if (user?.uid) {
      const unsubscribe = setupSalesListener();
      // Cleanup function to unsubscribe when component unmounts or user changes
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [user?.uid, setupSalesListener]);

  // Reset package page when applied filters change
  useEffect(() => {
    setPackagePage(1);
  }, [appliedFilters]);

  // Toggle tooltip package status between published and draft
  const togglePackageStatus = async (packageId: string, currentStatus: boolean) => {
    try {
      const newStatus = currentStatus ? 'draft' : 'published';
      const packageRef = doc(db, `tooltipPackages/${packageId}`);
      
      await updateDoc(packageRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      
      // Real-time listener will automatically update the UI
      console.log(`Package ${packageId} status updated to: ${newStatus}`);
    } catch (error) {
      console.error('Error updating package status:', error);
    }
  };

  // Handle delete package click - show confirmation modal
  const handleDeletePackage = (packageData: TooltipPackage) => {
    setPackageToDelete(packageData);
    setDeleteConfirmModal(true);
  };

  // Delete tooltip package from database
  const deleteTooltipPackage = async () => {
    if (!packageToDelete || isDeleting) return;
    
    setIsDeleting(true);
    try {
      const packageId = packageToDelete.packageId;
      
      // Delete the package document
      const packageRef = doc(db, `tooltipPackages/${packageId}`);
      await deleteDoc(packageRef);
      
      // Remove packageId from user's createdPackages array
      if (user?.uid) {
        try {
          const userRef = doc(db, `users/${user.uid}`);
          await updateDoc(userRef, {
            createdPackages: arrayRemove(packageId),
          });
        } catch (userUpdateError) {
          console.error('Error removing package from user document:', userUpdateError);
          // Don't fail the whole operation if user update fails
        }
      }
      
      // Close modal and reset state
      setDeleteConfirmModal(false);
      setPackageToDelete(null);
      
      // Real-time listener will automatically update the UI
      console.log(`Package ${packageId} deleted successfully`);
    } catch (error) {
      console.error('Error deleting package:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsDeleting(false);
    }
  };

  // Cancel handler for delete modal
  const onCancelDelete = () => {
    setDeleteConfirmModal(false);
    setPackageToDelete(null);
    setIsDeleting(false);
  };

  // Message to show in delete modal
  const deleteMessage = packageToDelete
    ? `Are you sure you want to delete "${packageToDelete.packageName}"? Once deleted, it cannot be recovered.`
    : undefined;

  return (
    <Layout>
      <div
        className="p-6 mx-auto bg-white rounded-lg shadow-sm"
        style={{
          height: "calc(100vh - 112px)",
          overflowY: "hidden",
          overflowX: "hidden"
        }}
      >
        <div className="flex justify-between items-center mb-1">
          <TabGroup
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          <button
            onClick={handleCreatePackage}
            className="flex w-[215px] h-[40px] items-center gap-2 bg-[#F15D18] hover:bg-orange-600 text-white px-4 py-2 rounded-[8px] text-[14px] nunito-bold transition-colors"
          >
            <Plus className="w-[24px] h-[24px" />
            Create Tooltip Package
          </button>
        </div>
        <SearchHeader
          title={activeTab === "package" ? "Tooltip Packages" : "Sales"}
          onSearch={handleSearchImpl}
          tooltipOptions={tooltipPackages.map((p) => ({ value: p.packageId, label: p.packageName }))}
          selectedTooltipTypes={selectedTooltipTypes}
          onTooltipTypeChange={handleTooltipTypeChange}
          hasTooltipSelect={activeTab !== "package"}
          hasPackageTooltipFilters={activeTab === "package"}
          hasSalesFilters={activeTab === "sales"}
          organizationOptions={uniqueOrgs}
          selectedOrganizations={selectedOrganizations}
          onOrganizationChange={handleOrganizationChange}
          onDateRangeChange={handleDateRangeChange}
          onRatingChange={handleRatingChange}
          appliedFilters={appliedFilters}
          onFilter={setAppliedFilters}
        />

        <div
          className="bg-white rounded-[12px] border-[1px] border-[#EBEBEB] overflow-hidden overflow-x-auto"
          style={{
            height: "calc(80vh - 150px)",
            overflowY: "auto",
            overflowX: "hidden"
          }}
        >
          <table className="min-w-full divide-y max-h-[700px] divide-gray-200">
            <thead className="bg-[#FEF0EA]">
              <tr>
                  {activeTab === "package" && (
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Status
                </th>)}
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Package ID
                </th>
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  <div className="flex items-center gap-2">

                  <span>
                  Package Name
                  </span>
                  <span className="col-span-1 flex flex-col">

                      <button onClick={() => handleSort('name', 'asc')}>
                        <FaSortUp className={`w-3 h-3 ${sortColumn === 'name' && sortDirection === 'asc' ? 'text-[#F15D18]' : 'text-gray-500'}`} />
                      </button>
                      <button onClick={() => handleSort('name', 'desc')}>
                        <FaSortDown className={`w-3 h-3 ${sortColumn === 'name' && sortDirection === 'desc' ? 'text-[#F15D18]' : 'text-gray-500'}`} />
                      </button>
                      </span>
                  </div>

                </th>
                   <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Package URL



                </th>
                {activeTab === "package" && (
                  <>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Description
                    </th>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Created
                    </th>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Last Sale
                    </th>
                  </>
                )}
                {activeTab === "sales" && (
                  <>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Organization Name
                    </th>
                    <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Purchased
                    </th>
                  </>
                )}
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Ratings
                </th>
                {activeTab === "package" && (
                  <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                    <div className="flex items-center gap-2">
                      <span>#Sales</span>
                      <span className="col-span-1 flex flex-col">

                      <button onClick={() => handleSort('sales', 'asc')}>
                        <FaSortUp className={`w-3 h-3 ${sortColumn === 'sales' && sortDirection === 'asc' ? 'text-[#F15D18]' : 'text-gray-500'}`} />
                      </button>
                      <button onClick={() => handleSort('sales', 'desc')} className="">
                        <FaSortDown className={`w-3 h-3 ${sortColumn === 'sales' && sortDirection === 'desc' ? 'text-[#F15D18]' : 'text-gray-500'}`} />
                      </button>
                      </span>
                    </div>
                  </th>
                )}
                {activeTab === "sales" && (
                  <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                    Reviews
                  </th>
                )}
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  <div className="flex items-center gap-2">
                    <span>Earnings</span>
                    {(activeTab === 'package' || activeTab === 'sales') ? (
                      <>
                        <span className="col-span-1 flex flex-col">

                        <button onClick={() => handleSort('earnings', 'asc')} className="">
                          <FaSortUp className={`w-3 h-3 ${sortColumn === 'earnings' && sortDirection === 'asc' ? 'text-[#F15D18]' : 'text-gray-500'}`} />
                        </button>
                        <button onClick={() => handleSort('earnings', 'desc')} className="p-">
                          <FaSortDown className={`w-3 h-3 ${sortColumn === 'earnings' && sortDirection === 'desc' ? 'text-[#F15D18]' : 'text-gray-500'}`} />
                        </button>
                        </span>
                      </>
                    ) : null}
                  </div>
                </th>
                  {activeTab === "package" && (
                <th className="px-2 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                  Action
                </th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(fetchingPackages || fetchingSales) ? (
                <tr>
                  <td colSpan={activeTab === "package" ? 10 : 9}>
                    <div className="flex flex-col items-center mt-[70px]">
                      <div className="text-[18px] nunito-semibold text-[#696969]">
                        {activeTab === "package" ? "Loading tooltip packages..." : "Loading sales data..."}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((item: TooltipPackage | TooltipSalesData, index: number) => (
                  <tr key={index} className="bg-white">
                      {activeTab === "package" && (
                    <td className="px-2 py-3 whitespace-nowrap">
                      <label className=" relative group inline-flex items-center cursor-pointer">
                        {/* Replace direct checkbox toggle with confirmation flow */}
                        <input
                          type="checkbox"
                          disabled={(item as TooltipPackage).admindisabled}
                          className="sr-only peer"
                          checked={(item as TooltipPackage).status}
                          onChange={() => {
                            // Open confirmation modal instead of immediate toggle
                            setStatusTargetPackage({
                              packageId: (item as TooltipPackage).packageId,
                              currentStatus: (item as TooltipPackage).status,
                              packageName: (item as TooltipPackage).packageName
                            });
                            setStatusConfirmModal(true);
                          }}
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full  peer-focus:ring-orange-300 peer-checked:bg-orange-500 peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                      {/* Hover tooltip shows enable/disable message dynamically */}
                <div className="absolute left-0  -translate-y-1/2 -top-8 px-2 py-1 w-[260px] bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30">
                 <div className="text-start">
                   {(item as TooltipPackage).admindisabled ? (
                     <>
                       <div className="nunito-regular text-[12px] text-[#696969] ">
                         This tooltip has been disabled by the admin.
                       </div>
                       <div className="text-[#696969] nunito-regular text-[12px]">
                         Please contact your admin if you would like
                       </div>
                       <div className="text-[#696969] nunito-regular text-[12px]">
                         to publish it again.
                       </div>
                     </>
                   ) : (item as TooltipPackage).status ? (
                     <>
                       <div className="nunito-regular text-[12px] text-[#696969] ">
                         Disabling will hide the tooltip package
                       </div>
                       <div className="text-[#696969] nunito-regular text-[12px]">
                         from new customers and make it
                       </div>
                       <div className="text-[#696969] nunito-regular text-[12px]">
                         unavailable for future sales.
                       </div>
                     </>
                   ) : (
                     <>
                       <div className="nunito-regular text-[12px] text-[#696969] ">
                         Enabling will make this tooltip package
                       </div>
                       <div className="text-[#696969] nunito-regular text-[12px]">
                         visible to new customers and available
                       </div>
                       <div className="text-[#696969] nunito-regular text-[12px]">
                         for future sales.
                       </div>
                     </>
                   )}
                 </div>
                </div>
                      </label>
                    </td>)}
                    <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                      <div className="relative inline-block group max-w-[160px]">
                        {/* Truncated visible text */}
                        <span className="block truncate" aria-label={(item as TooltipPackage).packageId || ''}>
                          {(((item as TooltipPackage).packageId || '').toString().length > 5)
                            ? `${((item as TooltipPackage).packageId || '').toString().slice(0, 5)}...`
                            : (item as TooltipPackage).packageId}
                        </span>

                        {/* Styled hover tooltip showing full packageId */}
                        <div className="absolute left-0 -translate-y-1/2 -top-4 px-2 py-1 min-w-max bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30 break-words">
                          {(item as TooltipPackage).packageId}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      
                      <div className="text-[14px] truncate max-w-36 nunito-semibold text-[#696969]">
                        {item.packageName}
                      </div>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap ">
                      <div className="relative inline-block group max-w-[160px]">

                      <div className="text-[14px] truncate max-w-36 nunito-semibold text-[#696969]">
                        {item.primaryUrl}
                      </div>
                            {/* Styled hover tooltip showing full packageId */}
                        <div className="absolute left-0 -translate-y-1/2 -top-4 px-2 py-1 min-w-max bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30 break-words">
                          {item.primaryUrl}
                        </div>
                      </div>

                    </td>
                    {activeTab === "package" && (
                      <>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                         
                          <label className=" relative group inline-flex items-center cursor-pointer">
                      <div className="text-[14px] nunito-semibold truncate max-w-44 text-[#696969]">
                       {(item as TooltipPackage).description}
                      </div>
                       <div className="absolute left-0 -top-4 -translate-y-1/2 px-2 py-1 w-[200px]  bg-[#FAFAFA]  nunito-regular text-[12px] text-[#696969] rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto pointer-events-none transition-opacity duration-200 z-30">
                          <div className="text-start break-words whitespace-pre-line">
                            <div className="nunito-regular text-[12px] text-[#696969] break-words whitespace-pre-line">
                             {(item as TooltipPackage).description}
                            </div>
                            </div>
                          
                      </div>
                      </label>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                          {(item as TooltipPackage).created}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                          {(item as TooltipPackage).lastSale}
                        </td>
                      </>
                    )}
                    {activeTab === "sales" && (
                      <>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular truncate max-w-36 text-[14px] text-[#696969]">
                          {(item as TooltipSalesData).customerName}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] truncate max-w-36 text-[#696969]">
                          {(item as TooltipSalesData).orgName}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                          {(item as TooltipSalesData).purchased}
                        </td>
                      </>
                    )}
                    <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                      <div className="flex items-center  ">
                        {item.ratings} <span className=" flex   ml-1"><Star className="text-[#F5A038] fill-[#F5A038] w-[24px] h-[24px]"/></span>
                      </div>
                    </td>
                    {activeTab === "package" && (
                      <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                        {(item as TooltipPackage).sales}
                      </td>
                    )}
                    {activeTab === "sales" && (
                      <div className="group relative">

                      <td className="px-2 py-3 whitespace-nowrap truncate max-w-28 nunito-regular text-[14px] text-[#696969]">
                        {(item as TooltipSalesData).reviews}
                      </td>
                             {/* Tooltip on hover */}
                <div className="absolute left-0 -translate-y-1/2 -top-4 px-2 py-1 w-[174px] bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30">
      <div className="text-start break-words whitespace-pre-line">
        <div className="nunito-regular text-[12px] text-[#696969] break-words whitespace-pre-line">
          {(item as TooltipSalesData).reviews}
        </div>
      </div>
    </div> 
                      </div>
                    )}
                    <td className="px-2 py-3 whitespace-nowrap nunito-regular text-[14px] text-[#696969]">
                      ${item.earnings}
                    </td>
                      {activeTab === "package" && (
                    <td className="px-2 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-1 hover:bg-gray-100 rounded"
                         
                          onClick={() => handleEditPackage(item as TooltipPackage)}
                        >
                          <Pencil className="h-5 w-5 text-[#919191]" />
                        </button>
                    

                        <button
                          className={`p-1 rounded group relative ${
                            (item as TooltipPackage).sales > 0 
                              ? 'cursor-not-allowed ' 
                              : ''
                          }`}
                         
                          disabled={(item as TooltipPackage).sales > 0}
                          onClick={() => {
                            if ((item as TooltipPackage).sales === 0) {
                              handleDeletePackage(item as TooltipPackage);
                            }
                          }}
                        >
                          <Trash className={`h-5 w-5 ${
                            (item as TooltipPackage).sales > 0 ? 'text-gray-400' : 'text-[#919191]'
                          }`} />
                          {/* Tooltip on hover */}

                          <div className="absolute right-0 -translate-y-1/2 -top-8 px-2 py-1  bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30">
  <>
                          { (item as TooltipPackage).sales > 0 && (
                          
                            <div className="text-start">
      <div className="nunito-regular text-[12px] text-[#696969] ">
        Sold tooltip packages cannot
      </div>
      <div className="text-[#696969] nunito-regular text-[12px]">
        be deleted. Only unsold
      </div>
      <div className="text-[#696969] nunito-regular text-[12px]">
        packages can be deleted.
      </div>
    </div>
    )}
  </>

  </div>
                        </button>
               
                          
                                <button
                                  className="text-orange-500 group relative underline text-[14px] nunito-semibold disabled:cursor-not-allowed disabled:text-gray-400"
                                  disabled={(item as TooltipPackage).tooltips.length === 0 || isLoading}
                                  onClick={async () => {
                                      // Cast item to TooltipPackage to access packageId and primaryUrl
                                      const pkg = item as TooltipPackage;
                                      // If no packageId or primaryUrl, do nothing
                                      if (!pkg?.packageId || !pkg?.primaryUrl) return;

                                      setIsLoading(true);
                                      setErrorMessage('');

                                      try {
                                        // Require extension to be installed and user logged in (same as edit flow)
                                        const extensionOk = await checkExtensionLogin();
                                        if (!extensionOk) {
                                          alert('Please ensure the extension is installed and you are logged in.');
                                          setIsLoading(false);
                                          return;
                                        }

                                        // Normalize URL and open in new tab
                                        const rawUrl = pkg.primaryUrl;
                                        const normalized = normalizeUrl(rawUrl);

                                        // Open in new tab
                                        window.open(normalized, '_blank');

                                        // Give the page a moment to load
                                        await new Promise(r => setTimeout(r, 1500));

                                        // Send injection request to extension for preview flow with tooltip_type
                                        const success = await sendTooltipRequest(pkg.packageId, 'preview_package', 3, 'preview');
                                        if (!success) {
                                          console.warn('Failed to send preview injection to extension after retries');
                                        }
                                      } catch (err) {
                                        console.error('Error initiating preview flow:', err);
                                        setErrorMessage('Error initiating preview.');
                                      } finally {
                                        setIsLoading(false);
                                      }
                                    }}
                                >
                                  View
                                  {/* Tooltip on hover */}

                          <div className="absolute right-0 -translate-y-1/2 -top-8 px-2 py-1  bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-30">
  <>
                          { (item as TooltipPackage).tooltips.length === 0 && (
                          
                            <div className="text-start">
      <div className="nunito-regular text-[12px] text-[#696969] ">
       Cannot view because tooltip is zero
      </div>
      
    </div>
    )}
  </>

  </div>
                                </button>
                      </div>
                    </td>)}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={activeTab === "package" ? 10 : 9}>
                    {/* Check if filters or search are applied */}
                    {searchQuery.trim() !== "" ||
                     selectedTooltipTypes.length > 0 ||
                     appliedFilters?.packageTooltipRating ||
                     appliedFilters?.salesRanges ||
                     appliedFilters?.earningsRanges ||
                     appliedFilters?.createdDateRange ||
                     appliedFilters?.lastSaleDateRange ||
                     selectedOrganizations.length > 0 ||
                     dateFrom !== "" ||
                     dateTo !== "" ||
                     selectedRating !== "" ? (
                      /* Show "No matching records found" when filters/search are applied */
                      <div className="flex flex-col items-center py-20">
                        <div className="w-24 h-24 mb-4 flex items-center justify-center">
                          <Search className="w-16 h-16 text-gray-400" />
                        </div>
                        <h2 className="text-[24px] nunito-semibold text-[#404040] mb-2">
                          Oops! No matching records found.
                        </h2>
                        <p className="text-[#919191] text-[14px] nunito-semibold mb-4 text-center">
                          Try changing the filters or search criteria to see more
                          results!
                        </p>
                      </div>
                    ) : (
                      /* Show default empty state when no filters are applied */
                      <div className="flex flex-col items-center mt-[70px]">
                        <div className="w-[416px] h-[214px] mb-4 flex items-center justify-center">
                          <img src={tooltipDataNotFoundImg} alt="data not found"/>
                        </div>
                        <h2 className="text-[24px] nunito-semibold mt-2 text-[#404040] ">
                         Oops! Nothing here yet.
                        </h2>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add Pagination */}
        {currentItems?.length > 0 && totalPages > 1 && (
          <div className="py-3 flex items-center justify-center">
            <nav className="flex space-x-1">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-2 py-1 text-[#696969] ${
                    currentPage === index + 1
                      ? "bg-[#FAFAFA] border-[#EBEBEB] rounded-[8px] border"
                      : ""
                  }`}
                >
                  {String(index + 1).padStart(2, "0")}
                </button>
              ))}
            </nav>
          </div>
        )}

        {createTooltipModel && (
          <div
            className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setCreateTooltipModel(false);
                setIsEditMode(false);
                setEditingPackageId(null);
                setFormData({
                  name: '',
                  primaryUrl: '',
                  price: '',
                  description: ''
                });
              }
            }}
          >
            <div
              className="bg-white rounded-[24px] p-8 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-[24px] nunito-semibold text-[#0A0A0A] text-center flex-1">
                  {isEditMode ? 'Edit Tooltip Package' : 'Tooltip Package Details'}
                </h1>
                <button
                  aria-label="Close modal"
                  className="ml-4 text-gray-500 hover:text-gray-700 bg-transparent p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                  onClick={() => {
                    setCreateTooltipModel(false);
                    setIsEditMode(false);
                    setEditingPackageId(null);
                    setFormData({
                      name: '',
                      primaryUrl: '',
                      price: '',
                      description: ''
                    });
                    setErrorMessage('');
                    setUrlError('');
                  }}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {errorMessage && (
                <div className="text-red-500 text-sm mb-4 text-center">
                  {errorMessage || urlError}
                </div>
              )}
              
              <div className=" border border-[#C4C4C4] rounded-[16px] p-6 mb-8">
                <div className="space-y-6">
                  {/* Tooltip Package Name */}
                  <div>
                    <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                      Tooltip Package Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-[#696969] rounded-[6px] text-[#404040] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter package name"
                    />
                  </div>

                  {/* Website URL */}
                  <div>
                    <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                      Website URL
                    </label>
                    <input
                      type="text"
                      value={formData.primaryUrl}
                      onChange={(e) => handleInputChange('primaryUrl', e.target.value)}
                      disabled={isEditMode}
                      className={`w-full px-4 py-3 ${
                        isEditMode ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                      } border ${
                        urlError ? "border-red-500" : "border-[#696969]"
                      } rounded-[6px] text-[#404040] focus:outline-none focus:ring-2 ${
                        urlError ? "focus:ring-red-500" : "focus:ring-orange-500"
                      } focus:border-transparent ${
                        isEditMode ? "opacity-60" : ""
                      }`}
                      placeholder="Enter website URL"
                    />
                    {urlError && (
                      <p className="text-red-500 text-sm mt-1">{urlError}</p>
                    )}
                 
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                      Price
                    </label>
                    <div className="flex items-center border border-[#696969] rounded-[6px] bg-white h-11 px-3 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent">
                      <span className="text-[#404040] text-[14px] nunito-regular ">$</span>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value.startsWith('0') || value === '0.' || value === '') {
                            handleInputChange('price', value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (['e', 'E', '-', '+', ','].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className="w-full bg-transparent outline-none text-[#404040] text-[14px] nunito-regular "
                        placeholder="Enter price"
                        step="0.01"
                        min="1"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-[#696969] rounded-[6px] text-[#404040] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent h-28 resize-y"
                      placeholder="Describe this tooltip package (what it includes, where it appears, etc.)"
                    />
                  </div>
                     {isEditMode && editingPackageId && (
                  <span
                    onClick={handleEditTooltips}
                    className={` underline text-[#F15D18] nunito-semibold text-[16px] cursor-pointer ' : ''}`}
                  >
                   Open Tooltip Editor                </span>
                )}
                </div>
              </div>

              {/* Buttons */}
              <div className=" flex justify-center">
             

                <button
                  onClick={handleNext}
                  disabled={isLoading}
                  className={`w-[120px] h-[40px] bg-[#F15D18] hover:bg-orange-600 text-white nunito-semibold text-[16px] py-1 px-2 rounded-[8px] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    isLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isLoading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update" : "Next")}
                </button>
              </div>
            </div>
          </div>
     )}

        {/* Delete Confirmation Modal (updated layout) */}
        {deleteConfirmModal && packageToDelete && (
          // Full-screen semi-opaque backdrop. Clicking the backdrop cancels the delete.
          <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={onCancelDelete}
          >
            <div
              className="gs-delete-flow-model bg-white rounded-lg p-6"
              style={{ maxWidth: "520px", width: "100%" }}
              onClick={(e) => e.stopPropagation() // prevent backdrop clicks from closing when interacting with modal
}
            >
              <span
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginLeft: "10px",
                  marginRight: "10px",
                  marginBottom: "20px",
                }}
              >
                <span
                  style={{
                    fontSize: "22px",
                    color: "#0A0A0A",
                    paddingBottom: "10px",
                    textAlign: "start",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                  }}
                >
                  {deleteMessage ||
                    "Are you sure you want to delete this? Once deleted, it cannot be recovered."}
                </span>
                <span>
                  <X
                    style={{
                      width: "24px",
                      height: "24px",
                      color: "#696969",
                      cursor: "pointer",
                      marginTop: "4px",
                    }}
                    onClick={onCancelDelete}
                  />
                </span>
              </span>

              <div style={{ gap: "20px", justifyContent: "center", display: "flex" }}>
                <button
                  onClick={onCancelDelete}
                  style={{
                    width: "230px",
                    borderRadius: "16px",
                    height: "60px",
                    border: "2px solid #F15D18",
                    fontSize: "24px",
                    color: "#F15D18",
                    marginRight: "20px",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={deleteTooltipPackage}
                  style={{
                    width: "230px",
                    borderRadius: "8px",
                    height: "60px",
                    backgroundColor: "#F15D18",
                    fontSize: "24px",
                    color: "#FFFFFF",
                    border: "none",
                    cursor: "pointer",
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Status Confirm Modal */}
        {statusConfirmModal && statusTargetPackage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={() => setStatusConfirmModal(false)}
          >
            <div
              className="bg-white rounded-lg p-6"
              style={{ maxWidth: "520px", width: "100%" }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-[20px] nunito-semibold text-[#0A0A0A] mb-4">
                {statusTargetPackage.currentStatus ? 'Disable Tooltip Package' : 'Enable Tooltip Package'}
              </h2>
              <p className="text-[16px] text-[#040404] mb-6">
                {statusTargetPackage.currentStatus
                  ? 'Disabling will hide this tooltip package from new customers and make it unavailable for future sales. Are you sure you want to proceed?'
                  : 'Enabling will make this tooltip package visible to new customers and available for future sales. Do you want to continue?'}
              </p>
             

              <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                <button
                  onClick={() => setStatusConfirmModal(false)}
                  className="w-[200px] h-[48px] border border-[#F15D18] text-[#F15D18] rounded-[8px] bg-transparent nunito-semibold"
                  disabled={statusChanging}
                >
                  No
                </button>

                <button
                  onClick={async () => {
                    if (statusChanging) return;
                    setStatusChanging(true);

                    // Optimistic UI update: flip the status locally so the user sees immediate change
                    const pkgId = statusTargetPackage.packageId;
                    try {
                      setTooltipPackages(prev => prev.map(p => p.packageId === pkgId ? { ...p, status: !statusTargetPackage.currentStatus } : p));

                      // Call backend to persist change
                      await togglePackageStatus(pkgId, statusTargetPackage.currentStatus);
                    } catch (e) {
                      console.error('Error toggling status via confirmation modal:', e);
                      // Revert optimistic update on error
                      setTooltipPackages(prev => prev.map(p => p.packageId === pkgId ? { ...p, status: statusTargetPackage.currentStatus } : p));
                    } finally {
                      setStatusChanging(false);
                      setStatusConfirmModal(false);
                      setStatusTargetPackage(null);
                    }
                  }}
                  className="w-[200px] h-[48px] bg-[#F15D18] text-white rounded-[8px] nunito-semibold"
                  disabled={statusChanging}
                >
                  {statusChanging ? 'Processing...' : 'Yes'}
                </button>
              </div>
            </div>
          </div>
        )}
     </div>
    </Layout>
  );
};

export default TooltipExpert;
